import moment from "moment";

function bagStatusNameFromBag(b) {
  if (!b || typeof b !== "object") return "";
  const o = b.bag_status ?? b.bagStatus ?? b.BagStatus;
  if (o == null) return "";
  if (typeof o === "string") return o;
  return o.name ?? o.label ?? "";
}

/**
 * Normalize case status from API string or form select object.
 * @param {unknown} raw
 * @returns {string}
 */
function normalizeCaseStatusString(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string") return raw;
  if (typeof raw === "object") {
    return raw.name ?? raw.label ?? "";
  }
  return String(raw);
}

/**
 * @param {object} opts
 * @param {string} [opts.type] Case type (e.g. UNCLAIMED)
 * @param {string} [opts.departureDate] Y-m-d
 * @param {string} [opts.flightDate] Y-m-d
 * @param {boolean} [opts.closed]
 * @param {string|{name?:string,label?:string}|null} [opts.caseStatus] `case_files.case_status` (e.g. DISPOSED)
 * @param {Array<{ receive_date?: string|null, forward_date?: string|null, deliver_date?: string|null, bag_status_name?: string }>} [opts.bags]
 * @returns {{ id: string, title: string, detail: string }[]}
 */
export function getCaseFileOperationalWarnings({
  type,
  departureDate,
  flightDate,
  closed,
  caseStatus,
  bags,
}) {
  if (normalizeCaseStatusString(caseStatus).trim().toUpperCase() === "DISPOSED") {
    return [];
  }

  const warnings = [];

  const dep = departureDate ? moment(departureDate).startOf("day") : null;

  if (dep && dep.isValid()) {
    const list = Array.isArray(bags) ? bags : [];
    for (const b of list) {
      const rxRaw = b?.receive_date;
      if (rxRaw == null || rxRaw === "") continue;
      const rx = moment(rxRaw).startOf("day");
      if (!rx.isValid()) continue;
      if (rx.isSame(dep, "day") || rx.isAfter(dep, "day")) {
        warnings.push({
          id: "receive-after-departure",
          title: "Receive on or after passenger departure",
          detail:
            "At least one bag has a receive date on the same calendar day as or after the passenger departure date.",
        });
        break;
      }
    }
  }

  const isUnclaimed = String(type || "").toUpperCase() === "UNCLAIMED";
  if (isUnclaimed && !closed && flightDate) {
    const fl = moment(flightDate).startOf("day");
    if (fl.isValid() && moment().diff(fl, "hours", true) >= 24) {
      warnings.push({
        id: "unclaimed-aging",
        title: "Unclaimed case past 24 hours",
        detail:
          "This unclaimed case is still open more than 24 hours after the recorded flight date.",
      });
    }
  }

  if (!closed) {
    const list = Array.isArray(bags) ? bags : [];
    for (const b of list) {
      const status = String(b?.bag_status_name ?? "").toUpperCase();
      if (status !== "RECEIVED") continue;
      const rxRaw = b?.receive_date;
      if (rxRaw == null || rxRaw === "") continue;
      const rx = moment(rxRaw);
      if (!rx.isValid()) continue;
      const hasForward =
        b.forward_date != null && String(b.forward_date).trim() !== "";
      const hasDeliver =
        b.deliver_date != null && String(b.deliver_date).trim() !== "";
      if (hasForward || hasDeliver) continue;
      if (moment().diff(rx, "hours", true) >= 48) {
        warnings.push({
          id: "received-no-progress-48h",
          title: "Received with no forward or delivery (48h+)",
          detail:
            "At least one bag is still in RECEIVED status with no forward or delivery recorded, 48 hours or more after its receive date.",
        });
        break;
      }
    }
  }

  return warnings;
}

export function normalizeBagsFromFormAndApi(formBags, apiBags) {
  const mapOne = (b) => ({
    receive_date: b.ReceiveDate ?? b.receive_date ?? null,
    forward_date: b.ForwardDate ?? b.forward_date ?? null,
    deliver_date: b.DeliverDate ?? b.deliver_date ?? null,
    bag_status_name: bagStatusNameFromBag(b),
  });
  if (Array.isArray(formBags) && formBags.length > 0) {
    return formBags.map(mapOne);
  }
  if (Array.isArray(apiBags) && apiBags.length > 0) {
    return apiBags.map(mapOne);
  }
  return [];
}
